import React from 'react';
import Head from 'next/head';
import PropTypes from 'prop-types';
import { getNumberOfProductsSEO } from 'utils/functions';
import { getCanonicalUrl, getBaseUrl } from 'utils/url-query';

const categoryPageSeoDefaultPropCategory = {};
const categoryPageSeoDefaultPropProducts = [];

const CategoryPageSeo = ({
  category = categoryPageSeoDefaultPropCategory,
  url = 'https://www.creoate.com',
  products = categoryPageSeoDefaultPropProducts,
  seoTitle = '',
  seoDescription = '',
}) => {
  const baseUrl = getBaseUrl(url);
  const canonical = getCanonicalUrl(url);

  const itemListElement = products.map((product, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    url: `${baseUrl}/product/${product?.productSlug}`,
    name: product?.productName,
  }));

  const jsonLd = {
    '@context': 'http://schema.org',
    '@type': 'itemList',
    itemListElement,
  };
  let itemListBreadcrumb = [
    {
      '@type': 'ListItem',
      position: '2',
      name: category?.name,
      item: `${baseUrl}/product-category/${category?.slug}`,
    },
  ];
  if (JSON.stringify(category?.parent) !== '{}') {
    itemListBreadcrumb = [
      {
        '@type': 'ListItem',
        position: '2',
        name: category?.parent?.name,
        item: `${baseUrl}/product-category/${category?.parent?.slug}`,
      },
      {
        '@type': 'ListItem',
        position: '3',
        name: category?.name,
        item: `${baseUrl}/product-category/${category?.slug}`,
      },
    ];
  }

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: '1',
        name: 'Home',
        item: baseUrl,
      },
      itemListBreadcrumb.map((itemlist) => itemlist),
    ],
  };

  const title =
    seoTitle || `Wholesale ${category?.name} Online for Resale from Independent Retailers, Brands in UK | Creoate`;
  const description =
    seoDescription ||
    `Wholesale ${category?.name}: Buy ${getNumberOfProductsSEO()} bulk ${
      category?.name
    } online for resale from independent wholesale suppliers across the UK, Europe at Creoate. Low min orders, Free shipping from 3000+ of approved vendors`;

  const SpeakableSpecificationJsonLd = {
    '@context': 'https://schema.org/',
    '@type': 'Article',
    name: category?.name,
    speakable: {
      '@type': 'SpeakableSpecification',
      xpath: ['/html/head/title', "/html/head/meta[@name='description']/@content"],
    },
    url: canonical,
  };

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />

        <meta name="viewport" content="initial-scale=1.0, width=device-width" />

        <meta property="og:locale" content="en_GB" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:site_name" content="CREOATE" />
        <meta
          property="og:image"
          content="https://www.creoate.com/_next/image?url=%2Fassets%2Fimages%2Flogo%2Ffull.webp&w=384&q=75"
        />
        <meta property="og:image:width" content="384" />
        <meta property="og:image:height" content="75" />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:site" content="@creoateuk" />
        <meta name="twitter:creator" content="@creoateuk" />

        <link rel="canonical" href={canonical} />

        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      </Head>

      <script
        id="seo-script-category-SpeakableSpecification"
        type="application/ld+json"
        data-turbolinks-track="reload"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(SpeakableSpecificationJsonLd),
        }}
      />

      <script
        id="seo-script-ItemList"
        type="application/ld+json"
        data-turbolinks-track="reload"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(jsonLd),
        }}
      />

      <script
        id="seo-script-Breadcrumb"
        type="application/ld+json"
        data-turbolinks-track="reload"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbJsonLd),
        }}
      />
    </>
  );
};

CategoryPageSeo.propTypes = {
  category: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
    parent: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
    }),
  }),
  url: PropTypes.string,
  products: PropTypes.instanceOf(Array),
  seoTitle: PropTypes.string,
  seoDescription: PropTypes.string,
};

export default CategoryPageSeo;
